// Operations owns catalogue identities. Presentation grouping never rewrites
// unit IDs, regions or grants, and unknown streams retain their own labels.
export interface ActivityProgram { id: string; name: string; unitIds: string[] }
export interface ActivityWriteScope { unitIds: string[]; regions: string[] }
interface ProgramUnit { id: string; name: string; type?: string }
interface ProgramActivity {
  owningUnitId?: string | null;
  owningUnit: string;
  region: string;
  allowedActions?: string[];
}
export function activityProgramName(activity: { owningUnitId?: string | null; owningUnit: string }, programs: ActivityProgram[] = []) {
  return programs.find(program => activity.owningUnitId && program.unitIds.includes(activity.owningUnitId))?.name || activity.owningUnit;
}
export function normalizeProgramFilters(values: string[], programs: ActivityProgram[] = [], units: Array<{id: string; name: string}> = []) {
  return [...new Set(values.map(value => activityProgramName({owningUnitId: units.find(unit => unit.name === value)?.id, owningUnit: value}, programs)))];
}

/** Pick a permitted creation pair, preferring the current program then region. */
export function activityCreationScope(units: ProgramUnit[], programs: ActivityProgram[], scopes: ActivityWriteScope[], preferredPrograms: string[] = [], preferredRegions: string[] = [], availableRegions: string[] = []) {
  const fallbackRegions = [...new Set([...preferredRegions, ...availableRegions, 'USA'])].filter(region => region && region !== '*');
  let selected: {unit: ProgramUnit; region: string; score: number; rank: number} | undefined;
  for (const unit of units) for (const scope of scopes) {
    if (!scope.unitIds.includes('*') && !scope.unitIds.includes(unit.id)) continue;
    const regions = scope.regions.includes('*') ? fallbackRegions : scope.regions.filter(region => region && region !== '*');
    for (const region of regions) {
      const name = activityProgramName({owningUnitId:unit.id, owningUnit:unit.name}, programs);
      const score = (preferredPrograms.includes(name) ? 2 : 0) + (preferredRegions.includes(region) ? 1 : 0);
      const catalogued = programs.find(program => program.unitIds.includes(unit.id));
      const rank = catalogued ? (catalogued.name === unit.name ? 2 : 1)
        : unit.type === 'workspace' ? -1 : ['program','service'].includes(unit.type || '') ? 1 : 0;
      if (!selected || score > selected.score || (score === selected.score && rank > selected.rank)) selected = {unit, region, score, rank};
    }
  }
  return selected ? {unit:selected.unit, region:selected.region} : undefined;
}

/** Resolve a display choice without granting access or migrating legacy IDs. */
export function activityProgramAssignment(activity: ProgramActivity, name: string, programs: ActivityProgram[], units: ProgramUnit[], scopes: ActivityWriteScope[] = []) {
  if (activityProgramName(activity, programs) === name)
    return {owningUnitId: activity.owningUnitId, owningUnit: activity.owningUnit};
  if (!activity.allowedActions?.includes('activity.update')) return undefined;
  const program = programs.find(program => program.name === name);
  const permitted = units.filter(unit => (program ? program.unitIds.includes(unit.id) : unit.name === name)
    && scopes.some(scope => (scope.unitIds.includes('*') || scope.unitIds.includes(unit.id))
      && (scope.regions.includes('*') || scope.regions.includes(activity.region))));
  // Prefer the canonical unit; a scope limited to a legacy unit still works.
  const target = permitted.find(unit => unit.name === name)
    || permitted.find(unit => unit.name.toLowerCase().endsWith(` - ${activity.region.toLowerCase()}`)) || permitted[0];
  return target ? {owningUnitId: target.id, owningUnit: target.name} : undefined;
}

/** Every changed member of a branch must have a permitted destination. */
export function activityProgramOptions(activities: ProgramActivity[], programs: ActivityProgram[], units: ProgramUnit[], scopes: ActivityWriteScope[] = []) {
  if (!activities.length) return [];
  const current = activityProgramName(activities[0], programs);
  const names = new Set([current, ...programs.filter(program => program.unitIds.length).map(program => program.name),
    ...units.filter(unit => unit.type !== 'workspace' && !programs.some(program => program.unitIds.includes(unit.id))).map(unit => unit.name)]);
  return [...names].filter(name => name === current || activities.every(activity => activityProgramAssignment(activity, name, programs, units, scopes)))
    .sort((left, right) => left.localeCompare(right));
}
